// Login screen. Two methods on one page (2026-10-19):
//
//   1. Username / email + password — the form at the top. Whether the
//      device's password manager may save and fill it is the Settings ›
//      Login screen switch (GET /login/methods `savedLogins`, 2026-10-29).
//      Off (the default — devices are shared): neither field looks like a
//      credential to the browser (components/login/savedLogins, SecretInput).
//      On: autocomplete="username" / "current-password" and a real password
//      field, so iOS / Android / Windows Hello offer to save it.
//   2. Colour + PIN — the LEGACY grid under it (components/login/ColourPinLogin),
//      shown only while GET /login/methods says someone still needs it.
//      Both methods end in the same completeLogin.
//
// A legacy account's first login lands on /account-setup (choose a
// username, set a policy password); after that only method 1 works for it.

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Info, Eye, EyeOff } from "lucide-react";
import { usePageTitle } from "../hooks/usePageTitle";
import { toast } from "react-toastify";
import { isLanguage, useTranslation } from "../i18n";
import { clearLocationCache } from "../components/helpers/sorterTwoLocationCache";
import { refreshScoreFromServer } from "../components/helpers/sessionScore";
import { markActive } from "../components/helpers/idleLogout";
import { takeLogoutReason } from "../components/helpers/logoutReason";
import { isManagerPlus } from "../access/positions";
import { apiFetch } from "../api/apiFetch";
import { setSessionToken } from "../api/apiFetch";
import LanguageToggle from "../components/LanguageToggle";
import ForgotPasswordButton from "../components/ForgotPasswordButton";
import LoginLinkButton from "../components/LoginLinkButton";
import ColourPinLogin from "../components/login/ColourPinLogin";
import TwoFactorStep from "../components/login/TwoFactorStep";
import SecretInput from "../components/login/SecretInput";
import { PASSWORD_MANAGER_OPT_OUT, rememberSavedLogins, savedLoginsFrom } from "../components/login/savedLogins";
import { isNetworkBlocked, loginErrorMessage } from "../components/login/loginErrors";
import {
    anySetupOutstanding, clearAccountSetup, clearRequirePasswordChange, setAccountSetup, setRequirePasswordChange,
} from "../components/helpers/authFlags";
import { dropTrustToken, readTrustToken } from "../components/helpers/twoFactorTrust";
import { isTwoFactorChallenge } from "../types/Auth";
import type { LoginMethods, LoginResponse, TwoFactorChallenge, TwoFactorCompletion } from "../types/Auth";
import "../styles/login.css";

// Last known answer from GET /login/methods, per device. Defaults to
// SHOWING the colour grid: an unmigrated operative on a Zebra facing a
// cold BE must never see a screen they cannot use, and the BE refuses a
// retired colour login anyway, so a stale "true" costs nothing. A device
// that was once told "false" never flashes the grid again.
const METHODS_KEY = "loginMethods";
const readCachedColourLogin = (): boolean => {
    try {
        const raw = window.localStorage.getItem(METHODS_KEY);
        if (!raw) return true;
        const parsed = JSON.parse(raw);
        return typeof parsed?.colourLogin === "boolean" ? parsed.colourLogin : true;
    } catch { return true; }
};
const cacheColourLogin = (value: boolean) => {
    try { window.localStorage.setItem(METHODS_KEY, JSON.stringify({ colourLogin: value })); } catch { /* ignore */ }
};

const Login = () => {
    usePageTitle("login");
    const navigate = useNavigate();
    const location = useLocation();
    const { t, setLanguage } = useTranslation();

    const [loading, set_loading] = useState(false);
    const [identifier, set_identifier] = useState("");
    const [password, set_password] = useState("");
    const [showPassword, set_showPassword] = useState(false);
    const [colourLogin, set_colourLogin] = useState<boolean>(readCachedColourLogin);
    // May the browser save / fill these fields? Not until the live answer
    // says so — deliberately not cached on the device (savedLogins.ts).
    const [savedLogins, set_savedLogins] = useState(false);
    const [pinStepActive, set_pinStepActive] = useState(false);
    // Two-factor (2026-10-20): set when the password (or the magic link)
    // passed but a code is still owed. While set, the card shows the
    // second step instead of the forms.
    const [challenge, set_challenge] = useState<TwoFactorChallenge | null>(null);

    // Synchronous guard against a double submit — set/checked before any
    // async work, so it closes the window that the async `loading` state
    // can't (state updates aren't applied until the next render).
    const submittingRef = useRef(false);

    // Post-login destination. Driven through an effect rather than calling
    // navigate() inline in the async handler, so the redirect always runs
    // after commit and is idempotent — it can't be lost to a timing gap.
    const [redirectTo, set_redirectTo] = useState<string | null>(null);
    useEffect(() => {
        if (redirectTo) navigate(redirectTo, { replace: true });
    }, [redirectTo, navigate]);

    // Explain an involuntary logout. Set by App.tsx when the BE stopped
    // recognising the session — almost always because the same account
    // signed in on another device (one session per user). Read-and-clear
    // on mount so it shows once. Rendered inline rather than as a toast:
    // toasts auto-close in 2.5s and this is the answer to "why am I
    // back at the login screen?".
    const [notice, set_notice] = useState<string | null>(null);
    useEffect(() => {
        const reason = takeLogoutReason();
        if (reason === "session_superseded") set_notice(t("login_signed_out_elsewhere"));
        else if (reason === "session_expired") set_notice(t("login_session_expired"));
        // Mount-only: t is stable for the render and re-running on a
        // language flip would re-read an already-cleared reason.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Which methods to offer. Any failure keeps the cached/default value.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await apiFetch(`/login/methods`, { method: "GET" });
                if (!res?.ok) return;
                const body = (await res.json().catch(() => null)) as LoginMethods | null;
                if (cancelled || !body) return;
                const allowSave = savedLoginsFrom(body);
                rememberSavedLogins(allowSave);
                set_savedLogins(allowSave);
                if (typeof body.colourLogin !== "boolean") return;
                set_colourLogin(body.colourLogin);
                cacheColourLogin(body.colourLogin);
            } catch { /* keep the cached/default answer */ }
        })();
        return () => { cancelled = true; };
    }, []);

    // Shared success path for every login flavour — username/email,
    // colour+PIN, the magic-link poller and the two-factor step hand the
    // same BE payload here.
    const completeLogin = (response: LoginResponse | TwoFactorCompletion) => {
        // A backup code just got used: say how many are left, once.
        if ((response as TwoFactorCompletion).usedBackupCode === true) {
            const left = (response as TwoFactorCompletion).backupCodesRemaining;
            toast.info(t("tfa_backup_code_used_warning", { count: String(typeof left === "number" ? left : 0) }), { toastId: "tfa-backup-used", autoClose: 8000 });
        }
        clearLocationCache();
        window.localStorage.setItem("isLoggedIn", "true");
        window.localStorage.setItem("user", JSON.stringify({
            uid: response.uid,
            // WIRE: `username` in the login response is the DISPLAY NAME
            // (legacy key). The login handle is `loginUsername`.
            name: response.username,
            username: response.loginUsername ?? null,
            warehouse: response.warehouse,
            permissions: response.permissions,
            // Normalised position name, when the BE returns one — kept
            // alongside `permissions` (which may still be a legacy rank)
            // so every position-aware helper prefers this field.
            position: response.position,
        }));
        // The account's saved Portal language (users.language) beats
        // whatever this browser was last switched to — it was either
        // seeded (Poland) or written by the user's own toggle, so it IS
        // their preference. null = none recorded: keep the device's
        // current language. Server-sourced, so apply locally only.
        if (isLanguage(response.language)) setLanguage(response.language, { persist: false });
        // Stash the signed session id so authenticated calls can
        // replay it as Authorization: Bearer when the cookie is
        // blocked (cross-site staging). On same-site prod the
        // cookie still works and the bearer is redundant.
        if (typeof response.sessionToken === "string") {
            setSessionToken(response.sessionToken);
        }
        markActive();
        refreshScoreFromServer().catch(() => { });

        // Forced rotation flag: stored whenever the BE sets it, so a
        // refresh can't escape it (ProtectedRoute bounces on it).
        if (response.requirePasswordChange) setRequirePasswordChange(response.passwordChangeReason ?? null);
        else clearRequirePasswordChange();

        // One-time account setup (legacy colour + PIN account, or a
        // Manager+ without an email). Takes precedence over rotation —
        // it includes the password step. The bearer (sessionToken,
        // captured above) authenticates the setup POST.
        if (anySetupOutstanding(response.accountSetup)) {
            setAccountSetup({
                username: response.accountSetup!.username === true,
                email: response.accountSetup!.email === true,
                password: response.accountSetup!.password === true,
                currentUsername: response.loginUsername ?? null,
            });
            set_redirectTo("/account-setup");
            return;
        }
        clearAccountSetup();

        if (response.requirePasswordChange) {
            set_redirectTo("/change-password");
            return;
        }

        // Return the user to the exact route ProtectedRoute bounced
        // them from (passed via location.state.from). A direct visit
        // to /login has no origin, so fall back to role-based
        // landing: admin/superAdmin to the admin dashboard, basic
        // users to the operational portal.
        const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
        const isAdmin = isManagerPlus(response.position ?? response.permissions);
        set_redirectTo(from ?? (isAdmin ? "/admin" : "/"));
    };

    // Every login flavour funnels through here (password form, colour grid,
    // magic-link poller). A challenged login shows the second step instead
    // of completing. Any trust token this device holds was evidently not
    // honoured (expired, or an admin reset), so it is dropped rather than
    // re-sent forever; the next accepted code stores a fresh one.
    const handleLoginResponse = (response: LoginResponse | TwoFactorChallenge) => {
        if (isTwoFactorChallenge(response)) {
            dropTrustToken();
            set_challenge(response);
            return;
        }
        completeLogin(response);
    };
    const leaveTwoFactor = useCallback(() => {
        set_challenge(null);
        set_password("");
    }, []);
    const twoFactorExpired = useCallback(() => {
        set_challenge(null);
        set_password("");
        toast.error(t("tfa_challenge_expired"), { toastId: "tfa-expired" });
    }, [t]);

    const handleIdentifierSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (submittingRef.current) return;

        const trimmed = identifier.trim();
        if (trimmed === "" || password === "") {
            toast.error(t("login_identifier_required"));
            return;
        }

        submittingRef.current = true;
        set_loading(true);
        try {
            // A trusted-device token (helpers/twoFactorTrust) lets an
            // enrolled Manager/Admin skip the code for 7 days.
            const trustToken = readTrustToken();
            const res = await apiFetch(`/login/user`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                // Trimmed, not lowercased — the BE normalises (it may be an email).
                body: JSON.stringify({ identifier: trimmed, password, ...(trustToken ? { trustToken } : {}) }),
            });
            const response = await res.json().catch(() => ({} as any));

            if (res.ok) {
                // Fields are deliberately NOT cleared before the redirect:
                // the browser pairs the saved credential with the submitted
                // form values.
                handleLoginResponse(response as LoginResponse | TwoFactorChallenge);
                return;
            }

            set_password("");
            // An allow-list refusal is shown full-screen by App (NetworkBlocked);
            // a toast underneath it would just be noise.
            if (!isNetworkBlocked(response)) {
                toast.error(loginErrorMessage(t, res.status, response, "identifier"));
            }
        } catch {
            // Network / CORS / cold-start failure: keep both fields so the
            // user can simply retry.
            toast.error(t("server_error"));
        } finally {
            submittingRef.current = false;
            set_loading(false);
        }
    };

    return (
        <div id="login-background">
            {/* Enrolment lays its two steps side by side on desktop: give the card the room. */}
            <div id="login-box" className={`animate-slideUp${challenge?.enrolmentRequired ? " login-box--wide" : ""}`}>
                <LanguageToggle className="login-language-toggle" />
                <h1 className="login-title">{t("portal_login")}</h1>

                {notice && (
                    <div className="login-notice" role="status">
                        <Info size={16} aria-hidden="true" />
                        <span>{notice}</span>
                    </div>
                )}

                {challenge && (
                    <TwoFactorStep
                        challenge={challenge}
                        onComplete={completeLogin}
                        onBack={leaveTwoFactor}
                        onExpired={twoFactorExpired}
                    />
                )}

                {!challenge && !pinStepActive && (
                    <form className="login-id-form" onSubmit={handleIdentifierSubmit} autoComplete={savedLogins ? undefined : "off"}>
                        <input
                            className="login-input"
                            type="text"
                            {...(savedLogins
                                ? { name: "username", autoComplete: "username" }
                                : { autoComplete: "off", ...PASSWORD_MANAGER_OPT_OUT })}
                            inputMode="email"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            enterKeyHint="next"
                            placeholder={t("login_identifier_placeholder")}
                            aria-label={t("login_identifier_label")}
                            value={identifier}
                            onChange={(e) => set_identifier(e.target.value)}
                            // The soft keyboard would cover the colour grid an
                            // unmigrated operative still needs; only grab focus
                            // once the grid is gone.
                            autoFocus={!colourLogin}
                            disabled={loading}
                        />
                        <div className="login-input-wrap">
                            <SecretInput
                                allowSave={savedLogins}
                                revealed={showPassword}
                                className="login-input"
                                name="password"
                                autoComplete="current-password"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                enterKeyHint="go"
                                placeholder={t("password")}
                                aria-label={t("password")}
                                value={password}
                                onChange={(e) => set_password(e.target.value)}
                                disabled={loading}
                            />
                            <button
                                type="button"
                                className="login-eye"
                                onClick={() => set_showPassword(s => !s)}
                                aria-label={showPassword ? t("change_password_hide") : t("change_password_show")}
                                tabIndex={-1}
                            >
                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                        <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                            {loading ? t("logging_in") : t("login")}
                        </button>
                    </form>
                )}

                {!challenge && colourLogin && !pinStepActive && (
                    <div className="login-divider"><span>{t("login_or_choose_colour")}</span></div>
                )}
                {!challenge && colourLogin && (
                    <ColourPinLogin
                        onLoggedIn={handleLoginResponse}
                        onActiveChange={set_pinStepActive}
                        allowSavedLogins={savedLogins}
                        compact
                    />
                )}

                {!challenge && !pinStepActive && (
                    <>
                        <ForgotPasswordButton className="login-forgot-password" />
                        <LoginLinkButton
                            className="login-forgot-password"
                            onLoggedIn={handleLoginResponse}
                        />
                    </>
                )}

            </div>

            {/* Logo + version live below the card now — keeps the box
                visually clean and groups the build identifier with the
                brand mark instead of the form. */}
            <div className="login-footer">
                <img
                    className="login-footer-logo"
                    src="/images/jli-logo-white.svg"
                    alt="JLI Trading"
                />
                <p className="login-version">0.26.0</p>
            </div>
        </div>
    );
};

export default Login;
